// src/pages/MyProfile/components/AvatarUploader.jsx
import React, { useState, useRef } from "react";
import {
    Box,
    Button,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    CircularProgress // Tambahkan CircularProgress
} from "@mui/material";
import { PhotoCamera } from '@mui/icons-material';
import { ref, update } from "firebase/database"; // Removed onValue as it's not used directly here

const AvatarUploader = ({ user, theme, database, currentAvatarUrl, setSnackbar }) => {
    const fileInputRef = useRef(null);

    // State untuk dialog pemrosesan gambar (drag & crop) untuk avatar
    const [imageToProcess, setImageToProcess] = useState(null);
    const [openImageProcessDialog, setOpenImageProcessDialog] = useState(false);
    const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
    const [isDraggingImage, setIsDraggingImage] = useState(false);
    const [startDragX, setStartDragX] = useState(0);
    const [startDragY, setStartDragY] = useState(0);
    const [scaledImageDimensions, setScaledImageDimensions] = useState({ width: 0, height: 0 });
    const imagePreviewRef = useRef(null);
    const [processingImage, setProcessingImage] = useState(false); // New state for loading

    const handleAvatarClick = () => {
        fileInputRef.current.click();
    };

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const img = new Image();
                img.src = reader.result;
                img.onload = () => {
                    const viewportSize = 250;

                    const scale = Math.max(viewportSize / img.width, viewportSize / img.height);

                    const calculatedScaledWidth = img.width * scale;
                    const calculatedScaledHeight = img.height * scale;

                    setImageToProcess(reader.result);
                    setOpenImageProcessDialog(true);

                    setImageOffset({
                        x: (viewportSize - calculatedScaledWidth) / 2,
                        y: (viewportSize - calculatedScaledHeight) / 2
                    });
                    setScaledImageDimensions({ width: calculatedScaledWidth, height: calculatedScaledHeight });
                };
            };
            reader.readAsDataURL(file);
        }
    };

    const handleMouseDown = (e) => {
        setIsDraggingImage(true);
        setStartDragX(e.clientX - imageOffset.x);
        setStartDragY(e.clientY - imageOffset.y);
    };

    const handleMouseMove = (e) => {
        if (!isDraggingImage) return;

        const newX = e.clientX - startDragX;
        const newY = e.clientY - startDragY;

        const viewportWidth = 250;
        const viewportHeight = 250;

        const maxX = Math.max(0, scaledImageDimensions.width - viewportWidth);
        const maxY = Math.max(0, scaledImageDimensions.height - viewportHeight);

        const clampedX = Math.max(-maxX, Math.min(0, newX));
        const clampedY = Math.max(-maxY, Math.min(0, newY));

        setImageOffset({ x: clampedX, y: clampedY });
    };

    const handleMouseUp = () => {
        setIsDraggingImage(false);
    };

    const handleSaveProcessedImage = async () => {
        if (!imageToProcess || !user) {
            setSnackbar({ open: true, message: "Tidak ada gambar untuk disimpan.", severity: "warning" });
            return;
        }

        setProcessingImage(true); // Start loading

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const finalAvatarSize = 120;
        const viewportSize = 250;

        canvas.width = finalAvatarSize;
        canvas.height = finalAvatarSize;

        const img = new Image();
        img.src = imageToProcess;

        img.onload = async () => {
            const displayScale = Math.max(viewportSize / img.width, viewportSize / img.height);

            const sourceX = -imageOffset.x / displayScale;
            const sourceY = -imageOffset.y / displayScale;

            const sourceWidth = viewportSize / displayScale;
            const sourceHeight = viewportSize / displayScale;

            ctx.drawImage(
                img,
                sourceX,
                sourceY,
                sourceWidth,
                sourceHeight,
                0, 0,
                finalAvatarSize,
                finalAvatarSize
            );

            const croppedBase64 = canvas.toDataURL('image/png');

            try {
                const userRef = ref(database, `db_user/tb_user/${user.uid}`);
                await update(userRef, { avatar: croppedBase64 });
                setSnackbar({ open: true, message: "Avatar berhasil diperbarui!", severity: "success" });
                setOpenImageProcessDialog(false);
                setImageToProcess(null);
                setImageOffset({ x: 0, y: 0 });
                setScaledImageDimensions({ width: 0, height: 0 });
            } catch (error) {
                console.error("Error updating avatar:", error);
                setSnackbar({ open: true, message: "Gagal memperbarui avatar.", severity: "error" });
            } finally {
                setProcessingImage(false); // Stop loading
            }
        };
    };

    const handleCloseImageProcessDialog = () => {
        setOpenImageProcessDialog(false);
        setImageToProcess(null);
        setImageOffset({ x: 0, y: 0 });
        setIsDraggingImage(false);
        setScaledImageDimensions({ width: 0, height: 0 });
    };

    return (
        <Box sx={{
            display: 'flex',
            borderRadius: '4px',
            borderColor: theme.palette.background.paper,
            mt: '-60px',
            mb: 2,
            width: 120,
            height: 120,
            overflow: 'hidden',
            position: 'relative',
            '&:hover .upload-icon-overlay': {
                opacity: 1,
            }
        }}>
            <Avatar
                src={currentAvatarUrl}
                sx={{
                    width: 120,
                    height: 120,
                    bgcolor: theme.palette.secondary.main,
                    border: 'none',
                }}
            >
                {user?.displayName ? user.displayName.charAt(0).toUpperCase() : ''}
            </Avatar>
            <input
                type="file"
                accept="image/jpeg, image/png"
                ref={fileInputRef}
                onChange={handleImageUpload}
                style={{ display: 'none' }}
            />
            <Box
                className="upload-icon-overlay"
                sx={{
                    position: 'absolute',
                    bottom: '12px',
                    right: '12px',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    bgcolor: 'white',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    opacity: 1,
                    transition: 'opacity 0.3s',
                    cursor: 'pointer',
                    border: `2px solid ${theme.palette.background.paper}`,
                    transform: 'translate(25%, 25%)',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                }}
                onClick={handleAvatarClick}
            >
                <PhotoCamera sx={{ fontSize: 16, color: theme.palette.primary.main }} />
            </Box>

            {/* Dialog untuk pemrosesan gambar (drag & crop) untuk avatar */}
            <Dialog open={openImageProcessDialog} onClose={handleCloseImageProcessDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Sesuaikan Gambar Profil</DialogTitle>
                <DialogContent dividers>
                    {imageToProcess && (
                        <Box
                            sx={{
                                width: 250,
                                height: 250,
                                overflow: 'hidden',
                                position: 'relative',
                                border: '1px solid #ccc',
                                cursor: isDraggingImage ? 'grabbing' : 'grab',
                                mx: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                        >
                            <img
                                ref={imagePreviewRef}
                                src={imageToProcess}
                                alt="Pratinjau"
                                style={{
                                    position: 'absolute',
                                    left: imageOffset.x,
                                    top: imageOffset.y,
                                    width: scaledImageDimensions.width,
                                    height: scaledImageDimensions.height,
                                    userSelect: 'none',
                                    pointerEvents: 'none',
                                }}
                            />
                        </Box>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                        Seret gambar untuk menyesuaikan posisinya.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseImageProcessDialog} disabled={processingImage}>Batal</Button>
                    <Button onClick={handleSaveProcessedImage} variant="contained" color="primary" disabled={processingImage}>
                        {processingImage ? <CircularProgress size={24} color="inherit" /> : "Simpan Gambar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AvatarUploader;
